/**
  * Credit purchase page
  */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuroraBackground from '../components/AuroraBackground';
import SEO from '../components/SEO';

// Note: This page uses Stripe Checkout (redirect), not Stripe.js elements
// So we don't need to load Stripe.js here - the backend provides the checkout URL

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface Plan {
    id: string;
    name: string;
    price_cents: number;
    price_display: string;
    credits: number;
    features: string[];
}

const CreditPurchasePage: React.FC = () => {
    const { getIdToken } = useAuth();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [highPrice, setHighPrice] = useState('24.99');
    const navigate = useNavigate();
    const location = useLocation();

    // Read redirect from query params
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirect');

    // Fetch plans
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/subscription/plans`);
                if (response.ok) {
                    const data = await response.json();
                    const fetchedPlans = data.plans || [];
                    setPlans(fetchedPlans);
                    if (fetchedPlans.length > 0) {
                        setHighPrice((fetchedPlans[0].price_cents / 100).toFixed(2));
                    }
                }
            } catch (err) {
                console.error('Error fetching plans:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
    }, []);

    const handlePurchase = async (planId: string) => {
        setIsCheckingOut(true);
        setError(null);

        try {
            const idToken = await getIdToken();
            if (!idToken) {
                throw new Error('Not authenticated');
            }

            // Create checkout session
            const response = await fetch(`${API_BASE_URL}/subscription/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                    success_url: `${window.location.origin}/purchase/success`,
                    cancel_url: `${window.location.origin}/plans`,
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to create checkout session');
            }

            const { checkout_url } = await response.json();

            // Redirect to Stripe Checkout
            window.location.href = checkout_url;
        } catch (err: any) {
            console.error('Checkout error:', err);
            setError(err.message || 'Failed to start checkout');
            setIsCheckingOut(false);
        }
    };

    if (isLoading) {
        return (
            <>
                <SEO
                    title="Purchase Credits | Vibe Commenting"
                    description="Purchase credits for AI-powered Instagram commenting. 500 credits for $29.99, never expire. Start growing today."
                    path="/plans"
                />
                <AuroraBackground />
                <div style={styles.container}>
                    <div style={styles.spinner} />
                </div>
            </>
        );
    }

    return (
        <>
            <SEO
                title="Purchase Credits | Vibe Commenting"
                description="Purchase credits for AI-powered Instagram commenting. 500 credits for $29.99, never expire. Start growing today."
                path="/plans"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": "Vibe Commenting Credits",
                    "description": "AI-powered Instagram commenting credits with auto-posting",
                    "offers": {
                        "@type": "AggregateOffer",
                        "lowPrice": "0",
                        "highPrice": highPrice,
                        "priceCurrency": "USD",
                        "offerCount": "2"
                    }
                }}
            />
            <AuroraBackground />
            <div style={styles.container}>
                <div style={styles.content}>
                <h1 style={styles.title}>Purchase Credits</h1>
                <p style={styles.subtitle}>
                    Power your AI-generated Instagram comments with credits
                </p>

                {error && (
                    <div style={styles.errorBox}>
                        {error}
                    </div>
                )}

                <div style={styles.plansGrid}>
                    {plans.map((plan) => (
                        <div key={plan.id} style={styles.planCard}>
                            <div style={styles.planHeader}>
                                <h2 style={styles.planName}>{plan.name}</h2>
                                <div style={styles.planPrice}>
                                    <span style={styles.priceAmount}>
                                        ${(plan.price_cents / 100).toFixed(2)}
                                    </span>
                                    <span style={styles.pricePeriod}>one-time</span>
                                </div>
                            </div>

                            <ul style={styles.featuresList}>
                                {plan.features.map((feature, index) => (
                                    <li key={index} style={styles.featureItem}>
                                        <svg
                                            style={styles.checkIcon}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M5 13l4 4L19 7" />
                                        </svg>
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handlePurchase(plan.id)}
                                disabled={isCheckingOut}
                                style={{
                                    ...styles.purchaseButton,
                                    opacity: isCheckingOut ? 0.7 : 1,
                                    cursor: isCheckingOut ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {isCheckingOut ? 'Processing...' : 'Buy Credits'}
                            </button>
                        </div>
                    ))}
                </div>

                <p style={styles.note}>
                    Credits never expire. Use them whenever you want.
                </p>

                <button
                    onClick={() => navigate(redirectTo || '/sessions')}
                    style={styles.maybeLaterButton}
                >
                    Maybe Later
                </button>
            </div>
        </div>
        </>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '100px 20px 40px',
    },
    content: {
        maxWidth: '800px',
        width: '100%',
        textAlign: 'center',
    },
    title: {
        fontSize: '40px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '16px',
    },
    subtitle: {
        fontSize: '18px',
        color: '#666666',
        marginBottom: '48px',
    },
    errorBox: {
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '24px',
        color: '#ef4444',
        fontSize: '14px',
    },
    plansGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '32px',
    },
    planCard: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '32px',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
        textAlign: 'left',
    },
    planHeader: {
        marginBottom: '24px',
    },
    planName: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '8px',
    },
    planPrice: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '4px',
    },
    priceAmount: {
        fontSize: '48px',
        fontWeight: 700,
        color: '#7C3AED',
    },
    pricePeriod: {
        fontSize: '16px',
        color: '#666666',
    },
    featuresList: {
        listStyle: 'none',
        padding: 0,
        margin: '0 0 32px 0',
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#333333',
        fontSize: '14px',
        padding: '8px 0',
    },
    checkIcon: {
        width: '20px',
        height: '20px',
        color: '#10B981',
        flexShrink: 0,
    },
    purchaseButton: {
        width: '100%',
        background: '#7C3AED',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    note: {
        fontSize: '14px',
        color: '#999999',
    },
    maybeLaterButton: {
        marginTop: '16px',
        background: 'transparent',
        border: 'none',
        color: '#7C3AED',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        padding: '8px 16px',
        textDecoration: 'underline',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#7C3AED',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};

export default CreditPurchasePage;
